import type { CSSProperties, ReactNode } from 'react';
import { createContext, useContext } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Modal — Radix dialog wrapper with compound Header/Body/Footer slots.
 *
 * A11y note: `Modal.Header` is required for screen-reader accessibility.
 * Radix logs a console warning at runtime if `DialogContent` has no
 * accessible name (no `DialogTitle` rendered). If you have a use case
 * for a header-less modal, render `<Modal.Header title={<VisuallyHidden>...</VisuallyHidden>} />`.
 */
export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  size?: ModalSize;
  /** When true, the body slot does NOT apply max-height + overflow-y. */
  disableBodyScroll?: boolean;
  children: ReactNode;
}

const SIZE_PX: Record<ModalSize, number> = {
  sm: 384,
  md: 512,
  lg: 672,
  xl: 896,
};

const overlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.5)',
  zIndex: 50,
};

function buildContentStyle(size: ModalSize): CSSProperties {
  return {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 'calc(100% - 32px)',
    maxWidth: `${SIZE_PX[size]}px`,
    background: 'var(--dg-surface, white)',
    border: '1px solid var(--dg-border, #e2e8f0)',
    borderRadius: 'var(--dg-r-md, 6px)',
    boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15), 0 4px 10px rgba(15, 23, 42, 0.08)',
    zIndex: 51,
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 64px)',
  };
}

const ModalScrollContext = createContext<{ disableBodyScroll: boolean }>({ disableBodyScroll: false });

function ModalRoot({ open, onOpenChange, size = 'md', children, disableBodyScroll }: ModalProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay style={overlayStyle} data-slot="dg-modal-overlay" />
        <DialogPrimitive.Content style={buildContentStyle(size)} data-slot="dg-modal-content">
          <ModalScrollContext.Provider value={{ disableBodyScroll: !!disableBodyScroll }}>
            {children}
          </ModalScrollContext.Provider>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

interface ModalHeaderProps {
  title: ReactNode;
  description?: ReactNode;
}

function ModalHeader({ title, description }: ModalHeaderProps) {
  return (
    <div
      data-slot="dg-modal-header"
      style={{
        padding: '20px 24px 12px',
        borderBottom: '2px solid var(--dg-brand-soft, #e0e7ff)',
      }}
    >
      <DialogPrimitive.Title
        style={{
          fontSize: 19,
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: 'var(--dg-brand-deep, #1e1b4b)',
          margin: 0,
        }}
      >
        {title}
      </DialogPrimitive.Title>
      {description && (
        <DialogPrimitive.Description
          style={{
            fontSize: 13,
            color: 'var(--dg-text-3, #64748b)',
            marginTop: 4,
          }}
        >
          {description}
        </DialogPrimitive.Description>
      )}
    </div>
  );
}

interface ModalBodyProps {
  children: ReactNode;
}

function ModalBody({ children }: ModalBodyProps) {
  const { disableBodyScroll } = useContext(ModalScrollContext);
  return (
    <div
      data-slot="dg-modal-body"
      style={{
        padding: '16px 24px',
        flex: 1,
        ...(disableBodyScroll
          ? {}
          : { overflowY: 'auto' }),
      }}
    >
      {children}
    </div>
  );
}

interface ModalFooterProps {
  children: ReactNode;
}

function ModalFooter({ children }: ModalFooterProps) {
  return (
    <div
      data-slot="dg-modal-footer"
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 8,
        padding: '12px 24px 16px',
        borderTop: '1px solid var(--dg-border, #e2e8f0)',
      }}
    >
      {children}
    </div>
  );
}

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
});
